import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  upsertLunchStatus,
  listLunchStatusesForDate,
  subscribeLunchStatusesForDate,
  fetchCurrentOrderForDate,
  fetchOrdersWithFilters,
  subscribeOrdersForDate,
  listOrderMembers,
  listOrderMembersForDate,
  subscribeOrderMembers,
  joinOrder,
  leaveOrder,
  LunchStatusValue,
  debugAllListeners,
  getFormattedTimeRemaining,
  isOrderExpired,
} from "../services/lunchBuddy";
import { getTodayDateString } from "../lib/date";
import CreateOrderPage from "../components/CreateOrderPage";
import { useOrderNotifications } from "../hooks/useOrderNotifications";
import { debugRealtimeStatus } from "../lib/supabase";
import toast from "react-hot-toast";

// Import test function
declare global {
  interface Window {
    testRealtimeConnection?: () => void;
  }
}

interface Props {
  userEmail: string;
  displayName?: string;
  token?: string;
}

function Section({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h3 className="mb-4 flex items-center gap-3 text-lg font-bold text-white">
        <span className="text-2xl">{icon}</span>
        {title}
      </h3>
      <div className="rounded-2xl border border-slate-700/40 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-6 shadow-lg backdrop-blur-sm">{children}</div>
    </section>
  );
}

export default function HomePage({ userEmail, displayName, token }: Props) {
  const today = useMemo(() => getTodayDateString(), []);
  const [statusByEmail, setStatusByEmail] = useState<Record<string, any>>({});
  const [homeList, setHomeList] = useState<any[]>([]);
  const [orderList, setOrderList] = useState<any[]>([]);
  const [eatOutList, setEatOutList] = useState<any[]>([]);
  const [myStatus, setMyStatus] = useState<LunchStatusValue | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [currentOrder, setCurrentOrder] = useState<any | null>(null);
  const [members, setMembers] = useState<string[]>([]);
  const [allOrderMembers, setAllOrderMembers] = useState<Record<string, string[]>>({});
  const [joiningOrders, setJoiningOrders] = useState<Set<string>>(new Set());
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  // Join modal state for entering dish nicely instead of prompt
  const [joinModal, setJoinModal] = useState<{ open: boolean; orderId: string | null; value: string }>(
    { open: false, orderId: null, value: "" }
  );
  // Track locally created orders to avoid double-adding when realtime 'create' arrives
  const ignoreNextCreateForIds = useRef<Set<string>>(new Set());
  // Manage order member subscriptions for all visible orders
  const memberSubsRef = useRef<Record<string, () => void>>({});
  // Uncontrolled input ref for join modal
  const joinInputRef = useRef<HTMLInputElement>(null);

  // Enable notifications when user is on home page
  useOrderNotifications({ userEmail, enabled: true });

  // Periodic check as fallback for realtime (every 30 seconds)
  useEffect(() => {
    const intervalId = setInterval(async () => {
      console.log("🔄 Periodic check: Refreshing order data...");
      try {
        // Load all orders for today
        const todayOrders = await fetchOrdersWithFilters({
          date: today,
          status: 'open', // Only open orders for homepage
          limit: 10
        });

        console.log(`🔍 Periodic check found ${todayOrders.length} orders:`, todayOrders);

        setOrders(todayOrders);

        // Set current order as the newest one
        const newestOrder = todayOrders.length > 0 ? todayOrders[0] : null;
        setCurrentOrder(newestOrder);

        // Update members for all orders (only members joined today)
        const allMembers: Record<string, string[]> = {};
        for (const order of todayOrders) {
          try {
            const list = await listOrderMembersForDate(order.id, today);
            allMembers[order.id] = list.map((m) => m.userEmail);
          } catch (error) {
            console.error(`Error loading members for order ${order.id}:`, error);
            allMembers[order.id] = [];
          }
        }
        setAllOrderMembers(allMembers);

        // Set main order members
        if (newestOrder) {
          setMembers(allMembers[newestOrder.id] || []);
        } else {
          setMembers([]);
        }
      } catch (error) {
        console.error("❌ Periodic check failed:", error);
      }
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, [today]);

  useEffect(() => {
    let unsubStatus: (() => void) | null = null;
    let unsubOrders: (() => void) | null = null;
    let unsubMembers: (() => void) | null = null;

    const load = async () => {
      // Debug realtime status
      console.log("🔍 Debugging realtime status on LunchBuddy load...");
      debugRealtimeStatus();
      const statusData = await listLunchStatusesForDate(today);
      setStatusByEmail(statusData.byEmail);
      setHomeList(statusData.home);
      setOrderList(statusData.order);
      setMyStatus(statusData.byEmail[userEmail]?.status || null);

      // Load all orders for today
      const todayOrders = await fetchOrdersWithFilters({
        date: today,
        status: 'open', // Only open orders for homepage
        limit: 10
      });

      setOrders(todayOrders);

      // Set current order as the newest one
      const current = todayOrders.length > 0 ? todayOrders[0] : null;
      setCurrentOrder(current);

      // Load members for all orders (only members joined today)
      const allMembers: Record<string, string[]> = {};
      for (const order of todayOrders) {
        try {
          const list = await listOrderMembersForDate(order.id, today);
          allMembers[order.id] = list.map((m) => m.userEmail);
        } catch (error) {
          console.error(`Error loading members for order ${order.id}:`, error);
          allMembers[order.id] = [];
        }
      }
      setAllOrderMembers(allMembers);

      // Set main order members
      if (current) {
        setMembers(allMembers[current.id] || []);
      }

      unsubStatus = subscribeLunchStatusesForDate(today, ({ action, record }) => {
        console.log("📊 Lunch status change detected:", { action, record });
        setStatusByEmail((prev) => {
          const next = { ...prev } as any;
          if (action === "delete") {
            delete next[record.userEmail];
          } else {
            next[record.userEmail] = record;
          }
          return next;
        });
      });

      unsubOrders = subscribeOrdersForDate(today, ({ action, record }) => {
        console.log(`🎯 LunchBuddy: Processing order ${action}:`, record);
        if (action === "create") {
          if (record.date !== today) return;
          const idKey = String(record.id);
          if (ignoreNextCreateForIds.current.has(idKey)) {
            // We already added this locally; update existing and clear the flag
            ignoreNextCreateForIds.current.delete(idKey);
            setOrders(prev => prev.map(o => (o.id === record.id ? record : o)));
          } else {
            // Add new order only if not already present
            setOrders(prev => {
              const exists = prev.some(o => o.id === record.id);
              return exists ? prev.map(o => (o.id === record.id ? record : o)) : [record, ...prev];
            });
          }
          // Set as current order
          setCurrentOrder(record);
          // Load members for the new order
          listOrderMembersForDate(record.id, today).then((list) => {
            const memberEmails = list.map((m) => m.userEmail);
            setMembers(memberEmails);
            setAllOrderMembers(prev => ({
              ...prev,
              [record.id]: memberEmails
            }));
          });
        } else if (action === "update") {
          if (record.date !== today) return;
          // Update order in list
          setOrders(prev => prev.map(order =>
            order.id === record.id ? record : order
          ));
          // Update current order if it matches
          setCurrentOrder((prevOrder: any) => {
            if (prevOrder && prevOrder.id === record.id) {
              console.log("✅ LunchBuddy: Updating current order with realtime data");
              return record;
            }
            return prevOrder;
          });
        } else if (action === "delete") {
          // Remove order from list
          setOrders(prev => prev.filter(order => order.id !== record.id));
          // Remove from allOrderMembers
          setAllOrderMembers(prev => {
            const next = { ...prev };
            delete next[record.id];
            return next;
          });
          // Remove current order if it matches
          setCurrentOrder((prevOrder: any) => {
            if (prevOrder && prevOrder.id === record.id) {
              console.log("✅ LunchBuddy: Removing current order due to realtime delete");
              setMembers([]);
              return null;
            }
            return prevOrder;
          });
        }
      });

      // members subscription set when order present
      if (current) {
        unsubMembers = subscribeOrderMembers(current.id, ({ action }) => {
          // simply refetch to keep it simple
          listOrderMembersForDate(current.id, today).then((list) => setMembers(list.map((m) => m.userEmail)));
        });
      }

      // Debug realtime status after subscriptions
      setTimeout(() => {
        console.log("🔍 Realtime status after subscriptions setup:");
        debugRealtimeStatus();
        debugAllListeners();
      }, 2000);
    };

    load();

    return () => {
      unsubStatus?.();
      unsubOrders?.();
      unsubMembers?.();
    };
  }, [today, userEmail]);

  // Auto-update derived states when statusByEmail changes
  useEffect(() => {
    const allStatuses = Object.values(statusByEmail) as any[];
    setHomeList(allStatuses.filter((r: any) => r.status === "home"));
    setOrderList(allStatuses.filter((r: any) => r.status === "order"));
    setEatOutList(allStatuses.filter((r: any) => r.status === "eat_out"));
    setMyStatus(statusByEmail[userEmail]?.status || null);
  }, [statusByEmail, userEmail]);

  // Keep 'members' of main order in sync with aggregated allOrderMembers map
  useEffect(() => {
    const mainOrder = orders.length > 0 ? orders[0] : null;
    if (mainOrder?.id) {
      setMembers(allOrderMembers[mainOrder.id] || []);
    } else {
      setMembers([]);
    }
  }, [orders, allOrderMembers]);

  // Subscribe to member changes for all displayed orders
  useEffect(() => {
    const currentIds = new Set(orders.map(o => String(o.id)));
    const subs = memberSubsRef.current;

    // Unsubscribe removed orders
    Object.keys(subs).forEach((orderId) => {
      if (!currentIds.has(orderId)) {
        try { subs[orderId]?.(); } catch { }
        delete subs[orderId];
      }
    });

    // Subscribe new orders
    orders.forEach(order => {
      const id = String(order.id);
      if (subs[id]) return;
      subs[id] = subscribeOrderMembers(order.id, ({ action, record }) => {
        setAllOrderMembers(prev => {
          const existing = prev[record.orderId] || [];
          if (action === 'create') {
            if (existing.includes(record.userEmail)) return prev;
            return { ...prev, [record.orderId]: [...existing, record.userEmail] };
          }
          if (action === 'delete') {
            // If orderId is not present (edge case), do a safe refetch for this order
            if (!record.orderId) {
              listOrderMembersForDate(order.id, today).then(list => {
                setAllOrderMembers(p => ({ ...p, [order.id]: list.map(m => m.userEmail) }));
              });
              return prev;
            }
            return { ...prev, [record.orderId]: existing.filter(e => e !== record.userEmail) };
          }
          return prev;
        });
      });
    });

    return () => {
      // On unmount, clean up all
      Object.values(memberSubsRef.current).forEach(unsub => {
        try { unsub(); } catch { }
      });
      memberSubsRef.current = {};
    };
  }, [orders]);

  const setStatus = async (value: LunchStatusValue) => {
    try {
      // Optimistic update - update UI immediately
      setMyStatus(value);

      // Update the statusByEmail immediately for responsive UI
      setStatusByEmail((prev) => ({
        ...prev,
        [userEmail]: {
          ...prev[userEmail],
          userEmail,
          status: value,
          date: today,
        },
      }));

      // Then make the actual API call
      await upsertLunchStatus(userEmail, value, today, displayName);
    } catch (error) {
      console.error("Error updating status:", error);
      // On error, the realtime subscription should revert to correct state
      toast.error("Có lỗi xảy ra khi cập nhật trạng thái");
    }
  };

  const mainOrder = orders.length > 0 ? orders[0] : null;

  const handleOrderCreated = (createdOrder: any) => {
    // Mark to ignore the next realtime 'create' for this ID
    try {
      ignoreNextCreateForIds.current.add(String(createdOrder.id));
    } catch { }
    setOrders(prev => {
      const exists = prev.some(o => o.id === createdOrder.id);
      return exists ? prev.map(o => (o.id === createdOrder.id ? createdOrder : o)) : [createdOrder, ...prev];
    });
    setCurrentOrder(createdOrder);
    setAllOrderMembers(prev => ({
      ...prev,
      [createdOrder.id]: [] // New order starts with no members
    }));
    setShowCreateOrder(false);
  };

  const handleCancelCreateOrder = () => {
    setShowCreateOrder(false);
  };

  // Show create order page if requested
  if (showCreateOrder) {
    return <CreateOrderPage userEmail={userEmail} displayName={displayName} onOrderCreated={handleOrderCreated} onCancel={handleCancelCreateOrder} />;
  }

  // Test realtime connection
  const testRealtime = () => {
    // Import test function dynamically
    import("../utils/testRealtimeManual")
      .then(({ testRealtimeConnection }) => {
        testRealtimeConnection();
      })
      .catch(console.error);
  };

  // Manual refresh function
  const manualRefresh = async () => {
    console.log("🔄 Manual refresh triggered...");
    try {
      // Refresh orders
      const todayOrders = await fetchOrdersWithFilters({
        date: today,
        status: 'open',
        limit: 10
      });
      console.log(`📊 Manual refresh found ${todayOrders.length} orders`);
      setOrders(todayOrders);

      // Refresh status data  
      const statusData = await listLunchStatusesForDate(today);
      setStatusByEmail(statusData.byEmail);

      // Update current order and load members for all orders
      const newestOrder = todayOrders.length > 0 ? todayOrders[0] : null;
      setCurrentOrder(newestOrder);

      // Load members for all orders (only members joined today)
      const allMembers: Record<string, string[]> = {};
      for (const order of todayOrders) {
        try {
          const list = await listOrderMembersForDate(order.id, today);
          allMembers[order.id] = list.map((m) => m.userEmail);
        } catch (error) {
          console.error(`Error loading members for order ${order.id}:`, error);
          allMembers[order.id] = [];
        }
      }
      setAllOrderMembers(allMembers);

      // Set main order members
      if (newestOrder) {
        setMembers(allMembers[newestOrder.id] || []);
      } else {
        setMembers([]);
      }

      toast.success("✅ Đã refresh thành công!");
    } catch (error) {
      console.error("❌ Manual refresh failed:", error);
      toast.error("❌ Lỗi khi refresh dữ liệu");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
        <div className="mx-auto max-w-4xl">
          <header className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-white">🍽️ Lunch Buddy</h1>
                <p className="text-slate-300 mt-1">
                  Xin chào, <span className="font-semibold text-emerald-400">{displayName || userEmail}</span>
                </p>
                {token && (
                  <div className="mt-2 max-w-xl rounded-md bg-slate-900/70 border border-slate-700 px-3 py-2">
                    <div className="text-xs font-semibold text-slate-400 mb-1">Teams SSO token:</div>
                    <div className="text-[11px] text-slate-300 break-all font-mono">
                      {token}
                    </div>
                  </div>
                )}

                {/* Debug & Refresh buttons */}
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={manualRefresh}
                    className="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 transition-colors"
                  >
                    🔄 Refresh
                  </button>

                  {/* Debug buttons - only show in development */}
                  {process.env.NODE_ENV === "development" && (
                    <>
                      <button
                        onClick={() => {
                          debugAllListeners();
                          debugRealtimeStatus();
                        }}
                        className="text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30 transition-colors"
                      >
                        🔍 Debug
                      </button>
                      <button onClick={testRealtime} className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition-colors">
                        🧪 Test Realtime
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Personal Status Selection - Compact */}
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => setStatus("home")}
                  className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200 ${myStatus === "home" ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg scale-105" : "bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 hover:scale-105"
                    }`}
                >
                  <span>🏠</span>
                  <span className="hidden sm:inline">Mang cơm</span>
                </button>
                <button
                  onClick={() => setStatus("order")}
                  className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200 ${myStatus === "order" ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg scale-105" : "bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 hover:scale-105"
                    }`}
                >
                  <span>🛒</span>
                  <span className="hidden sm:inline">Đặt đồ ăn</span>
                </button>
                <button
                  onClick={() => setStatus("eat_out")}
                  className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200 ${myStatus === "eat_out" ? "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg scale-105" : "bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 hover:scale-105"
                    }`}
                >
                  <span>🍽️</span>
                  <span className="hidden sm:inline">Ra ngoài ăn</span>
                </button>
              </div>
            </div>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Quick Stats */}
            <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border border-emerald-500/20 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-emerald-500/20 p-2">
                    <span className="text-xl">🏠</span>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{homeList.length}</div>
                    <div className="text-xs text-emerald-300">Mang cơm</div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-blue-500/20 p-2">
                    <span className="text-xl">🛒</span>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{orderList.length}</div>
                    <div className="text-xs text-blue-300">Đặt đồ ăn</div>
                  </div>
                </div>
              </div>


              <div className="rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-amber-500/20 p-2">
                    <span className="text-xl">🍽️</span>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{Object.values(statusByEmail).filter((r: any) => r.status === 'eat_out').length}</div>
                    <div className="text-xs text-amber-300">Ra ngoài ăn</div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-orange-500/20 p-2">
                    <span className="text-xl">🍜</span>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{orders.length}</div>
                    <div className="text-xs text-orange-300">Phiên mở</div>
                  </div>
                </div>
              </div>


              <div className="rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-purple-500/20 p-2">
                    <span className="text-xl">👥</span>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{members.length}</div>
                    <div className="text-xs text-purple-300">Đã tham gia</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Section title="Phiên đặt đồ ăn" icon="🍜">
            {orders.length > 0 ? (
              <div className="space-y-4">
                {orders.map((order, index) => {
                  const orderMembers = members.filter((_, i) => i === 0); // For simplicity, using same members for demo
                  const isUserMember = members.includes(userEmail);
                  const isMainOrder = index === 0; // First order is considered main

                  return (
                    <div
                      key={order.id}
                      className={`rounded-xl border p-6 transition-all ${isMainOrder
                          ? 'bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/20 shadow-lg'
                          : 'bg-gradient-to-br from-slate-700/50 to-slate-800/50 border-slate-600/30'
                        }`}
                    >
                      <div className="mb-6">
                        {/* Header with link button in top-right corner */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="rounded-full bg-orange-500/20 p-3">
                              <span className="text-2xl">🏪</span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="text-xl font-bold text-white">{order.restaurant}</h4>
                                {isMainOrder && (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                    🎯 Chính
                                  </span>
                                )}
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${order.status === 'open' ? 'bg-green-500/20 text-green-300' :
                                    order.status === 'closed' ? 'bg-red-500/20 text-red-300' :
                                      'bg-yellow-500/20 text-yellow-300'
                                  }`}>
                                  {order.status === 'open' ? '🟢 Đang mở' :
                                    order.status === 'closed' ? '🔴 Đã đóng' :
                                      '🟡 Đang xử lý'}
                                </span>
                              </div>
                              <p className="text-sm text-orange-300">
                                Tạo bởi {order.createdBy === userEmail ? "Bạn" : (order.author || order.createdBy.split("@")[0])}
                              </p>
                            </div>
                          </div>

                          {/* Action buttons in top-right corner */}
                          <div className="flex-shrink-0 ml-4 flex gap-2">
                            {order.link && (
                              <a
                                href={order.link.startsWith('http') ? order.link : `https://${order.link}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-all text-sm font-medium shadow-lg hover:shadow-blue-500/25 hover:scale-105"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <span className="text-lg">🔗</span>
                                <span>Xem Menu</span>
                              </a>
                            )}
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const shareUrl = `${window.location.origin}/order/${order.id}`;
                                
                                if (navigator.share) {
                                  try {
                                    await navigator.share({
                                      title: `Order tại ${order.restaurant}`,
                                      text: `Tham gia order tại ${order.restaurant}`,
                                      url: shareUrl
                                    });
                                  } catch (error) {
                                    console.error('Error sharing:', error);
                                  }
                                } else {
                                  try {
                                    await navigator.clipboard.writeText(shareUrl);
                                    toast.success('Đã copy link order vào clipboard');
                                  } catch (error) {
                                    console.error('Error copying to clipboard:', error);
                                    toast.error('Không thể copy link');
                                  }
                                }
                              }}
                              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/30 transition-all text-sm font-medium shadow-lg hover:shadow-emerald-500/25 hover:scale-105"
                            >
                              <span className="text-lg">📤</span>
                              <span>Chia sẻ</span>
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
                            <span className="text-xl">⏰</span>
                            <div>
                              <div className="text-xs text-slate-400">Thời gian còn lại</div>
                              <div className={`font-bold ${isOrderExpired(order) ? "text-red-400" : "text-white"}`}>
                                {getFormattedTimeRemaining(order)}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
                            <span className="text-xl">👥</span>
                            <div>
                              <div className="text-xs text-slate-400">Đã tham gia</div>
                              <div className="font-bold text-white">
                                {(allOrderMembers[order.id] || []).length} người
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
                            <span className="text-xl">📅</span>
                            <div>
                              <div className="text-xs text-slate-400">Ngày tạo</div>
                              <div className="font-bold text-white">{order.date}</div>
                            </div>
                          </div>
                        </div>

                        {/* Link section */}
                        {order.link && (
                          <div className="mb-6">
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
                              <span className="text-xl">🔗</span>
                              <div className="flex-1">
                                <div className="text-xs text-slate-400 mb-1">Link quán ăn</div>
                                <a
                                  href={order.link.startsWith("http") ? order.link : `https://${order.link}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-300 underline font-medium text-sm break-all"
                                >
                                  Xem menu & đặt hàng
                                </a>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Notes section */}
                        {order.notes && (
                          <div className="mb-6">
                            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50">
                              <span className="text-xl">📝</span>
                              <div className="flex-1">
                                <div className="text-xs text-slate-400 mb-1">Ghi chú</div>
                                <div className="text-sm text-slate-200 whitespace-pre-wrap">{order.notes}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Join/Leave button for each order */}
                      {order.status === 'open' && !isOrderExpired(order) ? (
                        (() => {
                          const orderMembers = allOrderMembers[order.id] || [];
                          const isUserMember = orderMembers.includes(userEmail);
                          const isJoining = joiningOrders.has(order.id);

                          const handleJoinLeave = async () => {
                            if (isJoining) return;

                            setJoiningOrders(prev => new Set(Array.from(prev).concat(order.id)));
                            try {
                              if (isUserMember) {
                                await leaveOrder(order.id, userEmail);
                                toast.success('Đã rời order');
                              } else {
                                // Open modal for dish input
                                setJoinModal({ open: true, orderId: order.id, value: "" });
                                return; // Defer rest to modal handler
                              }

                              // Reload members for this order
                              const list = await listOrderMembersForDate(order.id, today);
                              const memberEmails = list.map((m) => m.userEmail);
                              setAllOrderMembers(prev => ({
                                ...prev,
                                [order.id]: memberEmails
                              }));

                              // Update main order members if this is main order
                              if (isMainOrder) {
                                setMembers(memberEmails);
                              }
                            } catch (error) {
                              console.error('Error joining/leaving order:', error);
                              toast.error('Có lỗi xảy ra khi tham gia/rời order');
                            } finally {
                              setJoiningOrders(prev => {
                                const newArray = Array.from(prev).filter(id => id !== order.id);
                                return new Set(newArray);
                              });
                            }
                          };

                          return (
                            <button
                              onClick={handleJoinLeave}
                              disabled={isJoining}
                              className={`w-full rounded-xl p-4 font-bold text-lg transition-all duration-300 ${isUserMember
                                  ? "bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white shadow-lg shadow-red-500/25"
                                  : "bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg shadow-indigo-500/25"
                                } hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              <div className="flex items-center justify-center gap-3">
                                {isJoining ? (
                                  <>
                                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>{isUserMember ? "Đang rời..." : "Đang tham gia..."}</span>
                                  </>
                                ) : (
                                  <>
                                    <span className="text-2xl">{isUserMember ? "👋" : "🎉"}</span>
                                    {isUserMember ? "Rời khỏi phiên" : "Tham gia ngay!"}
                                  </>
                                )}
                              </div>
                            </button>
                          );
                        })()
                      ) : order.status === 'open' && isOrderExpired(order) ? (
                        // Expired order - show disabled message
                        <div className="text-center py-4">
                          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400">
                            <span className="text-2xl">⏰</span>
                            <div>
                              <div className="font-bold">Đã hết thời gian</div>
                              <div className="text-sm text-red-300">Không thể tham gia thêm</div>
                            </div>
                          </div>
                        </div>
                      ) : order.status !== 'open' ? (
                        // Closed/processing orders
                        <div className="text-center">
                          <a
                            href="/orders"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-all text-sm font-medium"
                          >
                            <span>👀</span>
                            Xem chi tiết
                          </a>
                        </div>
                      ) : null}
                    </div>
                  );
                })}

                {/* View all orders link */}
                {orders.length > 1 && (
                  <div className="text-center pt-4">
                    <a
                      href="/orders"
                      className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-500 hover:to-slate-600 text-white font-medium transition-all hover:scale-105"
                    >
                      <span>📋</span>
                      Xem tất cả {orders.length} orders
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center">
                <div className="mb-8">
                  <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 mb-4">
                    <span className="text-5xl">🍽️</span>
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-3">Chưa có phiên đặt đồ ăn</h4>
                  <p className="text-slate-400 text-lg mb-8">Tạo order mới để mọi người cùng tham gia</p>

                  <button
                    onClick={() => setShowCreateOrder(true)}
                    className="inline-flex items-center gap-3 rounded-2xl bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-xl py-4 px-8 transition-all duration-300 hover:scale-105 active:scale-95 shadow-xl shadow-orange-500/25"
                  >
                    <span className="text-3xl">🚀</span>
                    Tạo phiên đặt đồ ăn
                  </button>
                </div>

                {/* Quick info */}
                <div className="max-w-md mx-auto">
                  <div className="rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 p-6">
                    <h5 className="font-bold text-white mb-4 flex items-center gap-2">
                      <span className="text-xl">💡</span>
                      Bạn có thể
                    </h5>
                    <div className="space-y-3 text-sm text-slate-300">
                      <div className="flex items-center gap-3">
                        <span className="text-orange-400">•</span>
                        <span>Chọn cửa hàng yêu thích</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-orange-400">•</span>
                        <span>Đặt thời gian chốt order</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-orange-400">•</span>
                        <span>Thêm ghi chú cho team</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Section>
          {/* Join Dish Modal */}
          {joinModal.open && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-slate-800 border border-slate-600 rounded-2xl p-6 w-full max-w-md">
                <div className="mb-4 flex items-center justify-between">
                  <h4 className="text-white font-bold flex items-center gap-2">
                    <span>🍽️</span>
                    Nhập tên món (tùy chọn)
                  </h4>
                  <button
                    className="text-slate-400 hover:text-white"
                    onClick={() => setJoinModal({ open: false, orderId: null, value: "" })}
                  >
                    ✖
                  </button>
                </div>
                <input
                  autoFocus
                  ref={joinInputRef}
                  type="text"
                  placeholder="Ví dụ: Bún bò, Cơm tấm, Trà sữa..."
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      const orderId = joinModal.orderId!;
                      const raw = joinInputRef.current?.value || '';
                      const val = raw.trim() || undefined;
                      try {
                        await joinOrder(orderId, userEmail, val, displayName);
                        toast.success(val ? 'Đã tham gia và lưu món của bạn' : 'Đã tham gia order');
                        const list = await listOrderMembersForDate(orderId, today);
                        const memberEmails = list.map((m) => m.userEmail);
                        setAllOrderMembers(prev => ({ ...prev, [orderId]: memberEmails }));
                        if (orders[0]?.id === orderId) setMembers(memberEmails);
                        setJoinModal({ open: false, orderId: null, value: "" });
                      } catch (err) {
                        console.error(err);
                        toast.error('Không tham gia được order');
                      }
                    }
                  }}
                  className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
                <div className="mt-4 flex gap-3 justify-end">
                  <button
                    className="px-4 py-2 rounded-xl bg-slate-700 text-slate-300 hover:bg-slate-600"
                    onClick={() => setJoinModal({ open: false, orderId: null, value: "" })}
                  >
                    Hủy
                  </button>
                  <button
                    className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={async () => {
                      const orderId = joinModal.orderId!;
                      const raw = joinInputRef.current?.value || '';
                      const val = raw.trim() || undefined;
                      try {
                        await joinOrder(orderId, userEmail, val, displayName);
                        toast.success(val ? 'Đã tham gia và lưu món của bạn' : 'Đã tham gia order');
                        const list = await listOrderMembersForDate(orderId, today);
                        const memberEmails = list.map((m) => m.userEmail);
                        setAllOrderMembers(prev => ({ ...prev, [orderId]: memberEmails }));
                        if (orders[0]?.id === orderId) setMembers(memberEmails);
                        setJoinModal({ open: false, orderId: null, value: "" });
                      } catch (err) {
                        console.error(err);
                        toast.error('Không tham gia được order');
                      }
                    }}
                  >
                    Tham gia
                  </button>
                </div>
              </div>
            </div>
          )}

          <Section title="Team hôm nay" icon="👥">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 p-6 border border-emerald-500/20">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-full bg-emerald-500/20 p-2">
                    <span className="text-xl">🏠</span>
                  </div>
                  <strong className="text-lg font-bold text-emerald-300">Mang cơm ({homeList.length})</strong>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-lg bg-slate-900/50">
                  {homeList.length === 0 ? (
                    <div className="flex items-center justify-center p-8 text-slate-400">
                      <div className="text-center">
                        <div className="text-3xl mb-2">😊</div>
                        <div className="text-sm">Chưa có ai mang cơm</div>
                      </div>
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-700/40">
                      {homeList.map((r: any) => (
                        <li key={r.id} className="flex items-center gap-3 p-3 hover:bg-slate-700/30 transition-colors">
                          <div className="h-2 w-2 rounded-full bg-emerald-400"></div>
                          <span className="text-sm font-medium text-slate-200">{r.displayName || r.userEmail}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-600/10 p-6 border border-blue-500/20">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-full bg-blue-500/20 p-2">
                    <span className="text-xl">🛒</span>
                  </div>
                  <strong className="text-lg font-bold text-blue-300">Đặt/Cần đồ ăn ({orderList.length})</strong>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-lg bg-slate-900/50">
                  {orderList.length === 0 ? (
                    <div className="flex items-center justify-center p-8 text-slate-400">
                      <div className="text-center">
                        <div className="text-3xl mb-2">🤔</div>
                        <div className="text-sm">Chưa có ai đặt đồ ăn</div>
                      </div>
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-700/40">
                      {orderList.map((r: any) => (
                        <li key={r.id} className="flex items-center gap-3 p-3 hover:bg-slate-700/30 transition-colors">
                          <div className="h-2 w-2 rounded-full bg-blue-400"></div>
                          <span className="text-sm font-medium text-slate-200">{r.displayName || r.userEmail}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 p-6 border border-amber-500/20">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-full bg-amber-500/20 p-2">
                    <span className="text-xl">🍽️</span>
                  </div>
                  <strong className="text-lg font-bold text-amber-300">Ra ngoài ăn ({eatOutList.length})</strong>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-lg bg-slate-900/50">
                  {eatOutList.length === 0 ? (
                    <div className="flex items-center justify-center p-8 text-slate-400">
                      <div className="text-center">
                        <div className="text-3xl mb-2">🥗</div>
                        <div className="text-sm">Chưa có ai ra ngoài ăn</div>
                      </div>
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-700/40">
                      {eatOutList.map((r: any) => (
                        <li key={r.id} className="flex items-center gap-3 p-3 hover:bg-slate-700/30 transition-colors">
                          <div className="h-2 w-2 rounded-full bg-amber-400"></div>
                          <span className="text-sm font-medium text-slate-200">{r.displayName || r.userEmail}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
